import { type ValidationResult } from 'types';
import {
    ArgoConnectionKindEnum,
    GetConnectionConfigByIdDocument,
} from 'types/db';
import { validateConnection } from './connections.validations';
import { gqlClient } from 'src/util/gql-client';
import type { EntityModifyParameters, ExtendedEntityParameters } from 'src/components/EntityDetail';
import type { Route } from 'vue-router';
import {
    type ConnectionEntity,
    connectionConfigDetailsSchema,
} from '@redviking/argonaut-util/types/connections/connection-config';
import { fromZodError } from 'zod-validation-error';
import { EntityDetailMode } from 'src/components/EntityDetail/mode';
import { apiTrpcHttpClient } from 'src/util/api-trpc';

export const saveConnection = async (payload: ExtendedEntityParameters<'connection'>): Promise<ValidationResult> => {
    const {
        mode,
        entity,
        oldEntity,
    } = payload;
    const validationResult = validateConnection(entity, oldEntity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }

    try {
        await apiTrpcHttpClient.connections.updateConnection.mutate({
            connectionEntity: entity,
            forkEntity: mode === EntityDetailMode.Fork,
        });
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Connection: "${JSON.stringify(err)}"`,
        };
    }
};

export const createConnection = async (payload: EntityModifyParameters<'connection'>): Promise<ValidationResult> => {
    const { entity } = payload;
    const validationResult = validateConnection(entity, null);
    if (validationResult.status !== 'success') {
        return validationResult;
    }
    try {
        const {
            connectionId,
            connectionConfigId,
        } = await apiTrpcHttpClient.connections.createConnection.mutate({
            connectionEntity: entity,
        });

        return {
            status: 'success',
            newRoute: {
                name: 'connectionMaintenence',
                params: {
                    connectionId,
                    connectionConfigId,
                },
            },
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Connection: "${JSON.stringify(err)}"`,
        };
    }
};

export const getConnection = async (to: Route): Promise<{ entity: ConnectionEntity }> => {
    if (to.query.mode === 'create') {
        const connectionId = to.params.connectionId;
        if (!connectionId) {
            throw new Error(`Requested route does not contain connection id. Received: "${JSON.stringify(to.params)}"`);
        }
        const entityData: ConnectionEntity = {
            name: '',
            id: connectionId,
            connectionConfig: {
                id: '',
                config: {
                    kind: ArgoConnectionKindEnum.Serial,
                    details: {
                        baud: '',
                    },
                },
            },
        };
        return {
            entity: entityData,
        };
    }
    const connectionConfigId = to.params.connectionConfigId;
    if (!connectionConfigId) {
        throw new Error(`Requested route does not contain connection config id. Received: "${JSON.stringify(to.params)}"`);
    }
    const {
        argo_connection_config_by_pk,
    } = await gqlClient.request({
        document: GetConnectionConfigByIdDocument,
        variables: {
            connectionConfigId,
        },
    });

    if (!argo_connection_config_by_pk) {
        throw new Error(`Connection config id "${connectionConfigId}" did not return any data`);
    }

    const configParseResult = connectionConfigDetailsSchema.safeParse({
        details: argo_connection_config_by_pk.config,
        kind: argo_connection_config_by_pk.connection.kind,
    });
    if (!configParseResult.success) {
        const zodError = fromZodError(configParseResult.error);
        throw new Error(`Encountered an error while attempting to parse the connection config: ${zodError.message}`);
    }

    const entity: ConnectionEntity = {
        id: argo_connection_config_by_pk.connection.id,
        name: argo_connection_config_by_pk.connection.name,
        connectionConfig: {
            config: configParseResult.data,
            id: argo_connection_config_by_pk.id,
        },
    };

    return {
        entity,
    };
};
